import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Tags, Plus, Edit, Trash2, Loader2 } from "lucide-react";

export default function MobiliarioCategoriasPage() {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ nombre: "", descripcion: "", color: "#6366f1", vida_util_anos: 5 });
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mobiliario/categorias`, getAuthHeaders());
      setCategorias(res.data || []);
    } catch (err) {
      toast.error("Error al cargar categorías");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { setEditing(null); setFormData({ nombre: "", descripcion: "", color: "#6366f1", vida_util_anos: 5 }); setShowDialog(true); };
  const openEdit = (cat) => { setEditing(cat); setFormData({ nombre: cat.nombre, descripcion: cat.descripcion || "", color: cat.color || "#6366f1", vida_util_anos: cat.vida_util_anos || 5 }); setShowDialog(true); };

  const handleSubmit = async () => {
    if (!formData.nombre) { toast.error("Nombre requerido"); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/mobiliario/categorias/${editing.id}`, formData, getAuthHeaders());
        toast.success("Categoría actualizada");
      } else {
        await axios.post(`${API}/mobiliario/categorias`, formData, getAuthHeaders());
        toast.success("Categoría creada");
      }
      setShowDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar categoría?")) return;
    try {
      await axios.delete(`${API}/mobiliario/categorias/${id}`, getAuthHeaders());
      toast.success("Eliminada"); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900"><Sidebar /><main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></main></div>;

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
        <div className="flex justify-between mb-6">
          <div><h1 className="text-3xl font-bold dark:text-white">Categorías de Mobiliario</h1><p className="text-slate-500">{categorias.length} categorías</p></div>
          <Button onClick={openNew} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"><Plus className="w-4 h-4 mr-2" />Nueva</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categorias.map(cat => (
            <Card key={cat.id} className="hover:shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: cat.color }} />
                  <span className="font-medium dark:text-white">{cat.nombre}</span>
                </div>
                {cat.descripcion && <p className="text-sm text-slate-500 mb-2">{cat.descripcion}</p>}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">{cat.total_activos || 0} activos • {cat.vida_util_anos || 5} años vida útil</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(cat)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(cat.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle><Tags className="w-5 h-5 inline mr-2" />{editing ? "Editar" : "Nueva"} Categoría</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div><Label>Nombre *</Label><Input value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})} /></div>
              <div><Label>Descripción</Label><Input value={formData.descripcion} onChange={e => setFormData({...formData, descripcion: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Color</Label><Input type="color" value={formData.color} onChange={e => setFormData({...formData, color: e.target.value})} /></div>
                <div><Label>Vida útil (años)</Label><Input type="number" value={formData.vida_util_anos} onChange={e => setFormData({...formData, vida_util_anos: parseInt(e.target.value) || 5})} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSubmit} disabled={saving} className="bg-[#0C314C] text-white">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
